import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  Button,
  CircularProgress,
  Divider,
} from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import InstallDesktopIcon from '@mui/icons-material/InstallDesktop';
import { isOfflineOptIn, setOfflineOptIn } from '../../offline/entities';
import {
  clearReplica,
  canPromptInstall,
  promptInstall,
  isStandalone,
  subscribeInstallAvailability,
  supportsInstallPrompt,
  isAppleMobile,
} from '../../offline';
import { unsyncedOpCount } from '../../offline/opQueue';

/**
 * Per-device opt-in for offline mode. Offline is "available" in this build (the
 * REACT_APP_OFFLINE_MODE flag is on) but inert until a vet enables it HERE — on
 * the specific device they want offline access on. Enabling downloads the
 * clinic's data to this device; disabling wipes that local copy. Toggling
 * reloads the app so the gate + service worker re-evaluate.
 */
const OfflineSettings: React.FC = () => {
  // Frozen at mount — the page reloads on any change, so this reflects reality.
  const [enabled] = useState(() => isOfflineOptIn());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Install guidance: re-render when the captured beforeinstallprompt changes.
  const [, setInstallTick] = useState(0);
  useEffect(() => subscribeInstallAvailability(() => setInstallTick((t) => t + 1)), []);
  const installed = isStandalone();

  const handleInstall = async () => {
    await promptInstall(); // 'appinstalled' / availability-change re-renders us
  };

  const handleToggle = async (next: boolean) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    if (!next) {
      // Don't silently discard un-synced field work — the wipe below destroys
      // the WHOLE op queue (pending AND parked conflict/error ops: queued SOAP
      // drafts, meds, invoices, recorded cash payments). Mirror the logout
      // guard: count + explicit confirm. "Toggle it off and on" is the most
      // plausible troubleshooting move, so this must never be a silent loss.
      let unsynced = 0;
      try {
        unsynced = await unsyncedOpCount();
      } catch {
        /* courtesy check — fall through to confirm-free disable */
      }
      if (
        unsynced > 0 &&
        !window.confirm(
          `You have ${unsynced} unsynced change${unsynced === 1 ? '' : 's'} that will be permanently discarded if you turn offline access off now. Reconnect first to sync them. Turn off anyway?`,
        )
      ) {
        setBusy(false);
        return; // user cancelled to keep their unsynced work
      }
      // Turning OFF: wipe the on-device replica FIRST so clinic data doesn't
      // linger. If the wipe fails, do NOT opt out / reload — keep offline on,
      // surface the error, and let the vet retry. PHI must not be orphaned on a
      // device the user believes is no longer holding it.
      try {
        await clearReplica();
      } catch {
        setError("Couldn't remove the offline copy from this device. Please try again.");
        setBusy(false);
        return;
      }
    }
    if (next && !isStandalone() && canPromptInstall()) {
      // The user just chose offline — this is the moment of intent, so offer
      // the one-click native install right here (best offline experience:
      // own window, durable storage, offline cold-launch). Whatever they
      // choose, the opt-in proceeds.
      try {
        await promptInstall();
      } catch {
        /* best-effort */
      }
    }
    setOfflineOptIn(next);
    // Reload so OFFLINE_ENABLED and the service-worker registration recompute.
    window.location.reload();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <CloudDoneIcon sx={{ color: '#8B4513' }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Offline mode
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Use StableTrack without an internet connection on this device — view patient history and
        record visits offline, then sync automatically when you reconnect.
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            disabled={busy}
            onChange={(e) => handleToggle(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 500 }}>
              Enable offline access on this device
            </Typography>
            {busy && <CircularProgress size={16} />}
          </Box>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {enabled ? (
        <Alert severity="success" sx={{ mt: 2 }}>
          Offline access is <strong>on for this device</strong>. Your clinic's data is kept on this
          device and stays in sync. Turning it off will remove that local copy.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          When you turn this on, StableTrack downloads your clinic's records to this device so they
          work offline. Only enable it on devices you trust.
        </Alert>
      )}

      {/* Install guidance — only AFTER offline is on (the recommended order:
          enable offline -> the toggle fires the native install prompt itself).
          This card is the catch-up path for users who skipped that prompt.
          Showing it BEFORE opt-in would invite doing the steps backwards.
          Hidden once installed. */}
      {enabled && !installed && (
        supportsInstallPrompt() ? (
          // Chromium (Chrome/Edge): one-click install, or address-bar fallback
          // if the prompt hasn't been captured yet this session.
          <Alert
            severity="info"
            icon={<InstallDesktopIcon fontSize="inherit" />}
            sx={{ mt: 2 }}
            action={
              canPromptInstall() ? (
                <Button color="inherit" size="small" variant="outlined" onClick={handleInstall}>
                  Install StableTrack
                </Button>
              ) : undefined
            }
          >
            {canPromptInstall()
              ? 'For the best offline experience, install StableTrack as an app on this device — it gets its own window and the most reliable offline storage.'
              : "For the best offline experience, install StableTrack as an app: open your browser's menu and choose “Install StableTrack” (or the install icon in the address bar)."}
          </Alert>
        ) : isAppleMobile() ? (
          // iOS/iPadOS: no install prompt in any browser (all WebKit); the path
          // is Safari's Share → Add to Home Screen.
          <Alert severity="warning" icon={<InstallDesktopIcon fontSize="inherit" />} sx={{ mt: 2 }}>
            To install StableTrack for offline use on this device, open it in <strong>Safari</strong>,
            tap the Share button, then “Add to Home Screen.” For the most reliable desktop offline
            experience, use <strong>Google Chrome</strong> or <strong>Microsoft Edge</strong> on a computer.
          </Alert>
        ) : (
          // Desktop Safari / Firefox: can't install the app — recommend Chromium.
          <Alert severity="warning" icon={<InstallDesktopIcon fontSize="inherit" />} sx={{ mt: 2 }}>
            This browser can’t install StableTrack as an app, so you won’t get an install prompt.
            For full offline use, open StableTrack in <strong>Google Chrome</strong> or{' '}
            <strong>Microsoft Edge</strong> and install it from there. You can still use the app
            online in this browser.
          </Alert>
        )
      )}
    </Box>
  );
};

export default OfflineSettings;
